import { GlobalWizardState, LOBState } from "stores/wizard/state";
import { observable, action, computed } from "mobx";
import { LxConfig } from "stores/wizard/config";
import { TypeaheadSelection } from "uitk-react-typeahead";
import { setCookie } from "components/utility/cookie";
import { LastSearchData, LXLastSearchData } from "stores/wizard/LastSearchData";

export class LxWizardState implements LOBState {
  private globalState: GlobalWizardState;

  @computed
  public get canTrack() {
    return this.globalState.canTrack;
  }

  public lastSearchData?: LXLastSearchData;

  @computed
  public get location() {
    return this.globalState.location;
  }

  @observable
  public config: LxConfig;

  @observable
  public subLOBState: string;

  //Since this LOB doesn't have a subLOB, setting this as empty so the queryParam is removed when navigating to this LOB
  @action
  public updateSubLOBState(subLOBState: string) {
    this.subLOBState = "";
  }

  @computed
  public get date() {
    return this.globalState.date;
  }

  // validations
  @observable
  public destinationInvalidKey: string | "";

  @action
  public validateDestinationField = () => {
    if (!this.globalState.validateDestinationField()) {
      this.destinationInvalidKey = this.config.location.destination.invalidLocationMessageKey;

      return false;
    }

    this.destinationInvalidKey = "";

    return true;
  };

  @observable
  public dateStartInvalidKey: string = "";

  @observable
  public dateEndInvalidKey: string = "";

  @action
  public validateMaxDateRange = () => {
    if (
      !this.globalState.validateMaxDateRange(
        this.date.start,
        this.date.end,
        this.config.date.maxDaysRange
      )
    ) {
      this.dateEndInvalidKey = this.config.date.invalidMaxDatesRangeMessageToken;

      return false;
    }

    return true;
  };

  @action
  public validateEndDateField = () => this.validateMaxDateRange();

  @action
  public resetValidations = () => {
    this.destinationInvalidKey = "";
    this.dateStartInvalidKey = "";
    this.dateEndInvalidKey = "";
  };

  public validateForm = () => {
    const validations: boolean[] = [this.validateDestinationField(), this.validateEndDateField()];

    return !validations.includes(false);
  };

  public setLastSearchCookie = () => {
    if (this.canTrack) {
      const destination = this.location.destination;
      const detinationMetaData = this.location.destination.metaData;
      const date = this.date;

      const lastSearchCookieObject: LastSearchData = {
        lob: this.config.lastSearch.lobKey,
        lx: {
          destination_value: destination.value ? encodeURIComponent(destination.value) : "",
          destination_regionID: detinationMetaData.regionId
            ? encodeURIComponent(detinationMetaData.regionId)
            : "",
          destination_ttla: detinationMetaData.ttla
            ? encodeURIComponent(detinationMetaData.ttla)
            : "",
          destination_countryCode: detinationMetaData.countryCode
            ? encodeURIComponent(detinationMetaData.countryCode)
            : "",
          destination_latLong: detinationMetaData.latLong
            ? encodeURIComponent(detinationMetaData.latLong)
            : "",
          start_date: date.start ? date.start.toString() : "",
          end_date: date.end ? date.end.toString() : ""
        }
      };

      setCookie({
        key: this.config.lastSearch.lastSearchKey,
        value: JSON.stringify(lastSearchCookieObject)
      });
    }
  };

  @action
  public updateDestinationFromCookie(
    value: string,
    regionID: string,
    countryCode: string,
    latLong: string,
    ttla: string
  ) {
    this.globalState.updateDestinationFromCookie(value, regionID, countryCode, latLong, ttla);
    this.destinationInvalidKey = "";
  }

  public updateStateFromCookie = () => {
    const lastSearchData = this.lastSearchData;

    if (this.canTrack && lastSearchData) {
      const destinationValue = decodeURIComponent(lastSearchData.destination_value);
      const startDateFromCookie = new Date(lastSearchData.start_date);
      const endDateFromCookie = new Date(lastSearchData.end_date);
      const today = new Date();
      const tomorrow = new Date();
      tomorrow.setDate(today.getDate() + 1);

      const startDateToUse = startDateFromCookie >= today ? startDateFromCookie : today;
      const endDateToUse = endDateFromCookie >= tomorrow ? endDateFromCookie : tomorrow;

      this.updateDestinationFromCookie(
        destinationValue !== "undefined" ? destinationValue : "",
        lastSearchData.destination_regionID ? lastSearchData.destination_regionID : "",
        lastSearchData.destination_countryCode ? lastSearchData.destination_countryCode : "",
        lastSearchData.destination_latLong ? lastSearchData.destination_latLong : "",
        lastSearchData.destination_ttla ? lastSearchData.destination_ttla : ""
      );
      this.updateDateSelection(startDateToUse, endDateToUse);
    }
  };

  // other actions
  @action
  public overrideConfig(callback: () => void): void {
    callback();
  }

  @action
  public updateDateSelection = (start: Date, end: Date) => {
    this.globalState.updateDateSelection(start, end);
    this.dateStartInvalidKey = "";
    this.dateEndInvalidKey = "";
  };

  @action
  public updateDestinationSelection = (selection: TypeaheadSelection) => {
    this.globalState.updateDestinationSelection(selection);
    this.destinationInvalidKey = "";
  };

  public submit = (event: React.FormEvent) => {
    const isValidForm = this.validateForm();
    if (!isValidForm) {
      event.preventDefault();
    } else if (this.canTrack) {
      this.setLastSearchCookie();
    }
  };

  constructor(config: LxConfig, globalState: GlobalWizardState, lastSearchData?: LastSearchData) {
    this.config = config;
    this.globalState = globalState;
    if (lastSearchData && this.canTrack) {
      this.lastSearchData = lastSearchData.lx;
      this.updateStateFromCookie();
    }
  }
}
